<script setup>
import { computed } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  activeItem: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    required: true,
  },
  unsavedLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['select']);

const selectedItem = computed(
  () => props.items.find(item => item.id === props.activeItem) || props.items[0]
);

const handleMobileSelect = event => {
  emit('select', event.target.value);
};
</script>

<template>
  <div class="lg:hidden">
    <label class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12">
      <span>{{ label }}</span>
      <div class="relative">
        <span
          v-if="selectedItem?.icon"
          class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          :class="selectedItem.icon"
          aria-hidden="true"
        />
        <select
          :value="activeItem"
          class="h-11 w-full appearance-none rounded-xl border border-n-weak bg-n-solid-2 py-2 pl-10 pr-10 text-sm font-medium text-n-slate-12 outline-none transition hover:border-n-strong focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
          @change="handleMobileSelect"
        >
          <option v-for="item in items" :key="item.id" :value="item.id">
            {{ item.label }}{{ item.dirty ? ` — ${unsavedLabel}` : '' }}
          </option>
        </select>
        <span
          class="i-lucide-chevrons-up-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
          aria-hidden="true"
        />
      </div>
    </label>
  </div>

  <aside class="hidden lg:block">
    <nav
      class="sticky top-4 flex w-56 flex-col gap-1 rounded-2xl border border-n-weak bg-n-solid-2 p-2"
      :aria-label="label"
    >
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        class="group flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition"
        :class="
          item.id === activeItem
            ? 'bg-n-brand/10 text-n-blue-11'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
        "
        :aria-current="item.id === activeItem ? 'page' : undefined"
        @click="emit('select', item.id)"
      >
        <span class="size-4 shrink-0" :class="item.icon" aria-hidden="true" />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium">
            {{ item.label }}
          </span>
          <span
            v-if="item.helper"
            class="mt-0.5 block line-clamp-1 text-xs text-n-slate-10"
          >
            {{ item.helper }}
          </span>
        </span>
        <span
          v-if="item.external"
          class="i-lucide-arrow-up-right size-3.5 shrink-0 text-n-slate-9 transition group-hover:text-n-blue-11"
          aria-hidden="true"
        />
        <span
          v-else-if="item.dirty"
          class="size-2 shrink-0 rounded-full bg-n-amber-9"
          :title="unsavedLabel"
          role="status"
        >
          <span class="sr-only">{{ unsavedLabel }}</span>
        </span>
      </button>
    </nav>
  </aside>
</template>
